import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Tabs from "@/components/controls/Tabs";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { FieldManagementTab as EntityFieldManagementTab } from "@/pages/settings/components/dealsSettings/FieldManagement/FieldManagementTab";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { useUpdateAccountSettingByKey } from "@/entities/settings/hooks";
import EnablePartnersModal from "@/pages/settings/components/partnersSettings/EnablePartnersModal";
import FieldManagementDisable from "@/assets/icons/FieldManagementDisable.png";

const TRANSLATION_KEY = "settings.partners.fieldManagementTab";
const ENABLE_PARTNERSHIPS_KEY = "enable_partnerships";

const ENTITY_BY_TAB: Record<string, EntityType> = {
  company: EntityType.PARTNER,
  contact: EntityType.PARTNER_CONTACT
};

export const FieldManagementTab: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("company");
  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
  const { enabled: showPartnerContacts } = useFeatureFlag(
    FeatureFlags.CRM_8228_PARTNER_CONTACTS_TABLE
  );
  const partnershipsEnabled = useEnablePartnerships();
  const updateSettingsByKey = useUpdateAccountSettingByKey();

  if (!partnershipsEnabled) {
    return (
      <div className="relative w-full">
        <img src={FieldManagementDisable} alt="" className="h-auto w-full" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center">
          <Text variant="lgSubtitle" className="normal-case">
            {t(`${TRANSLATION_KEY}.emptyState.title`)}
          </Text>
          <Text variant="lgBodySecondary" className="max-w-[360px]">
            {t(`${TRANSLATION_KEY}.emptyState.description`)}
          </Text>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsEnableModalOpen(true)}
              data-test="partners-field-management-enable"
            >
              {t(`${TRANSLATION_KEY}.emptyState.enable`)}
            </Button>
          </div>
        </div>
        <EnablePartnersModal
          isOpen={isEnableModalOpen}
          setIsOpen={setIsEnableModalOpen}
          onConfirm={() =>
            updateSettingsByKey.mutate({
              pathParams: { settingKey: ENABLE_PARTNERSHIPS_KEY },
              data: { value: true }
            })
          }
        />
      </div>
    );
  }

  // Partner contact fields are managed under People settings once the
  // partner-contacts feature is enabled, so Partners settings shows only
  // the partner company fields.
  if (showPartnerContacts) {
    return <EntityFieldManagementTab entityType={EntityType.PARTNER} />;
  }

  const toggleOptions: Record<string, string> = {
    company: t(`${TRANSLATION_KEY}.toggle.company`),
    contact: t(`${TRANSLATION_KEY}.toggle.contact`)
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <Tabs
        options={toggleOptions}
        value={tab}
        valueSelected={setTab}
        size="lg"
        containerClassName="w-fit"
        dataTest="partners-field-management-entity-toggle"
      />
      <EntityFieldManagementTab key={tab} entityType={ENTITY_BY_TAB[tab]} />
    </div>
  );
};
